import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ComponentRef,
  computed,
  effect,
  EventEmitter,
  input,
  OnDestroy,
  Output,
  signal,
  ViewChild,
  ViewContainerRef,
  WritableSignal
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { mapFormItemToComponent, SupportedFormItemComponents } from '../../map-form-item-to-component';
import { ResourceData, UserInformation } from '@lfw/roesberg.liveforms.core';
import { EditorSettings } from '../../types/editor-settings';
import { ItemMenuComponent, MenuAction } from '../../item-menu/item-menu.component';
import { CommonModule } from '@angular/common';
import { extractXmlProps, formatXML, getPropsFrom } from '../../../../utils/xmlParser';
import { DEFAULT_BG_COLOR, extractColorProperties, getArgbToRgba, getTextColor } from '../../../../utils/colors';
import { IHasInputColorControls, IHasRangeColorControls } from '../InteractiveItem/interactive-item-form.component';

const SHARED_FIRST_COLUMN_WIDTHS_KEY = 'sharedFirstColumnWidths' as const;
const RESOURCE_DATA_CHANGED_KEY = 'resourceDataChanged' as const;
const ADD_NEW_INSTANCE_REQUIRED_KEY = 'addNewInstanceRequired' as const;
const DELETE_INSTANCE_REQUIRED_KEY = 'deleteInstanceRequired' as const;
const UPDATE_FIRST_COLUMN_WIDTH_REQUIRED_KEY = 'updateFirstColumnWidthRequired' as const;
const IS_VISIBLE_KEY = 'isVisible' as const;
const HAS_INPUT_COLOR_CONTROL_KEY = 'hasInputColorControl' as const;
const HAS_RANGE_COLOR_CONTROL_KEY = 'hasRangeColorControl' as const;
const PROPERTIES_KEY = 'properties' as const;
const INPUT_BG_COLOR_KEY = 'inputBgColor' as const;

@Component({
  selector: 'lfe-dynamic-form-item',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, ItemMenuComponent],
  templateUrl: './dynamic-form-item.component.html',
  standalone: true,
  styleUrl: './dynamic-form-item.component.css'
})
export class DynamicFormItemComponent implements AfterViewInit, OnDestroy {
  @ViewChild('dynamicContainer', { read: ViewContainerRef })
  container!: ViewContainerRef;

  xml = input.required<ChildNode>();
  form = input.required<FormGroup>();
  user = input.required<UserInformation>();
  settings = input.required<EditorSettings>();
  itemIndex = input.required<number>();
  isItemCreatedInCurrentSession = input.required<(itemId: string) => boolean>();
  sectionItemId = input.required<string>();
  filteredXmlItems = input.required<ChildNode[]>();
  filteredXmlItemsQty = input.required<number>();
  firstSiblingId = input.required<string>();
  sharedFirstColumnWidthsInput = input<WritableSignal<Record<string, number>>>();
  isReadonly = input(false);
  debug = input(false);
  orderId = input.required<string>();

  @Output() addCommentRequested = new EventEmitter<{ itemId: string }>();
  @Output() addPhotoCommentRequested = new EventEmitter<{ itemId: string }>();
  @Output() deleteItemRequested = new EventEmitter<{ itemId: string }>();
  @Output() resourceDataChanged = new EventEmitter<{ photoItemId: string; resourceData: ResourceData[] }>();
  @Output() repeatInstanceAddRequested = new EventEmitter<{
    repeatItemId: string;
    repeaterItemId: string;
  }>();
  @Output() repeatInstanceDeleteRequested = new EventEmitter<{
    repeatGroupSectionItemId: string;
  }>();
  @Output() updateFirstColumnWidth = new EventEmitter<{
    id: string;
    width: number;
  }>();

  public componentRef: ComponentRef<SupportedFormItemComponents> | null = null;

  showMenu = signal(false);

  isChildVisible = signal(true);

  itemType = computed(() => this.xml().nodeName);

  isItemInputColorControlEnabled = signal(false);
  isItemSelectionColorControlEnabled = signal(false);

  itemInputColor = computed(() => {
    const props = getPropsFrom(this.xml());
    const { inputColor } = extractColorProperties(props);
    return getArgbToRgba(inputColor).substring(0, 7);
  });

  itemSelectionColor = signal(getArgbToRgba(DEFAULT_BG_COLOR).substring(0, 7));

  sharedFirstColumnWidths = computed(() => this.sharedFirstColumnWidthsInput()?.());

  currentInputBgColor = signal(DEFAULT_BG_COLOR);

  hasMenuHidden = computed(() => {
    const itemType = this.xml().nodeName;
    const excludedTypes = ['SectionEndItem', 'DividerItem'];
    return this.isReadonly() || excludedTypes.includes(itemType);
  });

  isCommentCreatedInCurrentSession = computed(() => {
    const itemId = this.getItemId();
    if (itemId) {
      return this.isItemCreatedInCurrentSession()(this.getItemId());
    }

    return false;
  });

  isCommentOwnedByCurrentUser = computed(() => {
    const itemId = this.getItemId();
    if (itemId) {
      const props = getPropsFrom(this.xml());
      const isCommentItem = this.itemType() === 'CommentItem';
      const isPhotoCommentItem = this.itemType() === 'PhotoItem' && props?.['AddedWhileProcessing'] === 'True';
      const isOwnComment = this.user().displayName === props?.['UserName'];
      return isOwnComment && (isCommentItem || isPhotoCommentItem);
    }

    return false;
  });

  // Effect to update component inputs reactively
  private _reactiveInputsEffect = effect(() => {
    if (
      this.sharedFirstColumnWidths() &&
      this.componentRef &&
      SHARED_FIRST_COLUMN_WIDTHS_KEY in this.componentRef.instance
    ) {
      const sharedFirstColumnWidths = this.sharedFirstColumnWidths();
      if (sharedFirstColumnWidths) {
        this.componentRef.instance.sharedFirstColumnWidths.set(sharedFirstColumnWidths);
      }
    }
  });

  ngAfterViewInit() {
    this.createComponent();
  }

  ngOnDestroy() {
    if (this.componentRef) {
      this.componentRef.destroy();
    }
  }

  private createComponent(): void {
    const xmlNode = this.xml();
    const nodeName = xmlNode.nodeName;

    const componentClass = mapFormItemToComponent[nodeName as keyof typeof mapFormItemToComponent];

    if (!componentClass) {
      console.error('No component class found for node:', nodeName);
      return;
    }

    this.componentRef = this.container.createComponent(componentClass);
    this.componentRef.setInput('xml', xmlNode);
    this.componentRef.setInput('form', this.form());
    this.componentRef.setInput('isReadonly', this.isReadonly());
    this.componentRef.setInput('user', this.user());
    this.componentRef.setInput('settings', this.settings());
    this.componentRef.setInput('sectionItemId', this.sectionItemId());
    this.componentRef.setInput('firstSiblingItemId', this.firstSiblingId());
    this.componentRef.setInput('renderedXmlItems', this.filteredXmlItems());
    this.componentRef.setInput('renderedXmlItemsQty', this.filteredXmlItemsQty());

    if (componentClass === mapFormItemToComponent['PhotoItem']) {
      this.componentRef.setInput('orderId', this.orderId());
    }

    // Set initial sharedFirstColumnWidths if available
    if (SHARED_FIRST_COLUMN_WIDTHS_KEY in this.componentRef.instance && this.sharedFirstColumnWidths) {
      const sharedFirstColumnWidths = this.sharedFirstColumnWidths();
      if (sharedFirstColumnWidths) {
        this.componentRef.instance.sharedFirstColumnWidths.set(sharedFirstColumnWidths);
      }
    }

    // Subscribe to visibility changes from the child component
    if (this.componentRef.instance.visibilityChanged) {
      this.componentRef.instance.visibilityChanged.subscribe((isVisible: boolean) => {
        this.isChildVisible.set(isVisible);
      });
    }

    // Subscribe to resource data changes from PhotoItem components
    if (RESOURCE_DATA_CHANGED_KEY in this.componentRef.instance) {
      this.componentRef.instance.resourceDataChanged.subscribe(
        (event: { photoItemId: string; resourceData: ResourceData[] }) => {
          this.resourceDataChanged.emit(event);
        }
      );
    }

    // Subscribe to RepeatItem new instance required events
    if (ADD_NEW_INSTANCE_REQUIRED_KEY in this.componentRef.instance) {
      this.componentRef.instance.addNewInstanceRequired.subscribe(
        ({ repeatItemId, repeaterItemId }: { repeatItemId: string; repeaterItemId: string }) => {
          this.repeatInstanceAddRequested.emit({
            repeatItemId,
            repeaterItemId
          });
        }
      );
    }

    // Subscribe to RepeatItem delete instance required events
    if (DELETE_INSTANCE_REQUIRED_KEY in this.componentRef.instance) {
      this.componentRef.instance.deleteInstanceRequired.subscribe(
        ({ repeatGroupSectionItemId }: { repeatGroupSectionItemId: string }) => {
          this.repeatInstanceDeleteRequested.emit({
            repeatGroupSectionItemId
          });
        }
      );
    }

    // Subscribe to update first column width required events
    if (UPDATE_FIRST_COLUMN_WIDTH_REQUIRED_KEY in this.componentRef.instance) {
      this.componentRef.instance.updateFirstColumnWidthRequired.subscribe(
        ({ id, width }: { id: string; width: number }) => {
          this.updateFirstColumnWidth.emit({ id, width });
        }
      );
    }

    // Set initial visibility state
    if (IS_VISIBLE_KEY in this.componentRef.instance) {
      this.isChildVisible.set(this.componentRef.instance.isVisible());
    }

    if (HAS_INPUT_COLOR_CONTROL_KEY in this.componentRef.instance) {
      this.isItemInputColorControlEnabled.set(this.componentRef.instance.hasInputColorControl);
    }

    if (HAS_RANGE_COLOR_CONTROL_KEY in this.componentRef.instance) {
      this.isItemSelectionColorControlEnabled.set(this.componentRef.instance.hasRangeColorControl);
    }

    if (PROPERTIES_KEY in this.componentRef.instance && INPUT_BG_COLOR_KEY in this.componentRef.instance.properties) {
      const inputBgColor = this.componentRef.instance.properties.inputBgColor as string;
      this.currentInputBgColor.set(inputBgColor || '');
    }
  }

  toggleMenu(): void {
    if (this.showMenu()) return this.closeMenu();
    return this.openMenu();
  }

  openMenu(): void {
    this.showMenu.set(true);
  }

  closeMenu(): void {
    this.showMenu.set(false);
  }

  hasComponentRefInputColor = computed(
    () => this.componentRef && HAS_INPUT_COLOR_CONTROL_KEY in this.componentRef.instance
  );
  hasComponentRefRangeColor = computed(
    () => this.componentRef && HAS_RANGE_COLOR_CONTROL_KEY in this.componentRef.instance
  );

  onMenuAction(action: MenuAction): void {
    switch (action.action) {
      case 'addComment':
        this.addCommentRequested.emit({ itemId: this.getItemId() });
        break;
      case 'addPhotoComment':
        this.addPhotoCommentRequested.emit({ itemId: this.getItemId() });
        break;
      case 'deleteItem':
        this.deleteItemRequested.emit({ itemId: this.getItemId() });
        break;
      case 'getXml':
        this.handleGetXml();
        break;
      case 'getFormattedXml':
        this.handleGetXml(true);
        break;
      case 'setInputColor':
        this.setInputColor(String(action.data));
        if (String(action.data) && !this.form().dirty) {
          this.form().markAsDirty();
        }
        break;
      case 'removeInputColor':
        this.removeInputColor();
        this.form().markAsDirty();
        break;

      case 'setSelectionColor':
        this.setSelectionColor(String(action.data));
        if (String(action.data) && !this.form().dirty) {
          this.form().markAsDirty();
        }
        break;
      case 'removeSelectionColor':
        this.removeSelectionColor();
        this.form().markAsDirty();
        break;
    }
  }

  private removeInputColor(): void {
    if (this.hasComponentRefInputColor()) {
      const instance = this.componentRef?.instance as IHasInputColorControls;
      instance.currentInputBgColor.set('');
      instance.currentInputTextColor.set('');
    }
  }

  private setInputColor(color: string): void {
    if (this.hasComponentRefInputColor()) {
      const instance = this.componentRef?.instance as IHasInputColorControls;
      instance.currentInputBgColor.set(color);
      instance.currentInputTextColor.set(getTextColor(color));
    }
  }

  private setSelectionColor(color: string): void {
    if (this.hasComponentRefRangeColor()) {
      const instance = this.componentRef?.instance as IHasRangeColorControls;
      instance.setRangeBackgroundColor(color);
    }
  }

  private removeSelectionColor(): void {
    if (this.hasComponentRefRangeColor()) {
      const instance = this.componentRef?.instance as IHasRangeColorControls;
      instance.setRangeBackgroundColor('');
    }
  }

  private handleGetXml(formatted = false): void {
    if (this.componentRef && this.componentRef.instance.getXML) {
      const xml = this.componentRef.instance.getXML();
      console.log(formatted ? formatXML(xml) : xml);
    } else {
      console.warn('getXML method not available for this item');
    }
  }

  private getItemId(): string {
    // Get the ID from the XML child elements (CustomID first, then ID)
    const element = this.xml() as Element;

    const props = extractXmlProps(element);

    if (props['CustomID'] || props['ID']) {
      return props['CustomID'] || props['ID'];
    }

    console.warn('No ID found for element:', element.nodeName);
    return '';
  }
}
